import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppSelector } from "redux/hooks";
import { useAuth } from "components/Auth";
import { RootState } from "redux/store";
import { useDispatch } from "react-redux";
import {
  NOTIFICATION_EVENT_TYPE,
  NotificationCountResponse,
  NotificationItem,
  NotificationListResponse,
  NotificationReadMark,
} from "api/interfaces/notificationInterface.interface";
import {
  getNotification,
  postFirstNotification,
  postNotificationRead,
  getNotificationCount as getNotificationCountAPI,
  postAllRead,
} from "api/notificationAPI";
import { setIsPopup } from "redux/reducers/menu/userPopup";
import {
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { updateNotiCount } from "redux/stomp/stompSlice";
import { StompMessageKind } from "redux/stomp/interface";
import * as s from "./NotificationStyle";
import NotificationButton from "./NotificationButton";
import NotificationPopup from "./NotificationPopup";
import useStatusMutation from "hook/useStatusMutation";
import { useInfiniteQuery, useQuery } from "react-query";
import type { QueryFunctionContext } from "react-query/types/core";
import { useMenu } from "hook/useMenu";
import { getHistoryIdByCommentId } from "api/alertAPI";
import { SUBSCRIPTION_WS_STATUS } from "redux/stomp/stompSubscriptionSlice";
import { baseQueryInfo } from "utils/queryUtil";

export default function Notification() {
  const isReciveNotiCount = useRef<boolean>(false);
  const auth = useAuth();
  const { user } = auth;
  const count = useAppSelector((state: RootState) => state.stomp.notiCount);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isUnReadOnly, setIsUnReadOnly] = useState<boolean>(false);
  const [isSettingOpen, setIsSettingOpen] = useState<boolean>(false);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);
  const notificationSettingRef = useRef<HTMLDivElement>(null);
  const openUserPopup: boolean = useAppSelector((state) => state.userPopup);
  const dispatch = useDispatch();
  const selectAccount = useAppSelector((state) => state.accountSettings);
  const { navigateSwitchAccount } = useMenu({});

  //현재 접속중인 user의 이메일 보내고 모든 account의 알림을 서버에서 카운트 조회
  useQuery(
    ["notificationCount", { email: user.email }],
    () => getNotificationCountAPI({ email: user.email }),
    {
      onSuccess: (data: NotificationCountResponse) => {
        if (typeof data?.notiCount === "number") {
          dispatch(updateNotiCount(data.notiCount));
        }
      },
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );

  //첫 리스트 조회시 첫 알림 보내기
  const mutateSendNotificationFirstItem = useStatusMutation(
    postFirstNotification,
    {
      onSuccess: (data: NotificationItem) => {
        dispatch(updateNotiCount(0));
        refetch();
      },
    }
  );

  const getHistoryId = async (item: NotificationItem) => {
    try {
      const { result: historyId } = await getHistoryIdByCommentId({
        accountId: item.accountId,
        refenceId: item.referenceId ?? "",
      });
      return historyId;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  //아이템 클릭 후 이동시 서버에 읽음 처리
  const onChangeReadStatus = async (
    item: NotificationItem
  ): Promise<NotificationReadMark | undefined> => {
    try {
      return await postNotificationRead({
        email: user.email,
        notiId: item.notiId,
        notiTime: item.notiTime,
        isRead: true,
      });
    } catch (error) {
      console.error(error);
    }
  };

  //알림 클릭시 이동
  const navigateToNotification = async (item: NotificationItem) => {
    onChangeReadStatus(item);
    if (
      item.kind === StompMessageKind.ORGANIZATION &&
      item.eventType === NOTIFICATION_EVENT_TYPE.DELETE
    ) {
      return;
    }
    let selectAccountId = item.accountId;
    if (
      item.kind === StompMessageKind.SUBSCRIPTION &&
      item.subKind === SUBSCRIPTION_WS_STATUS.REQUESTED
    ) {
      selectAccountId = item.targetAccountId ?? "";
    }
    if (!selectAccountId) return;
    let path = "";
    let queryString: Record<string, string> = {};
    switch (item.kind) {
      case StompMessageKind.PLAN:
        path = "/subscription/usage";
        break;
      case StompMessageKind.ASSIGN:
        path = `/alert/history/detail`;
        queryString = {
          recorderId: item.kindId,
        };
        break;
      case StompMessageKind.COMMENT:
        let historyId = item.referenceId ? await getHistoryId(item) : null;
        path = `/alert/history/detail${item.referenceId ? `/view` : ""}`;
        queryString = {
          recorderId: item.kindId,
        };
        if (item.referenceId && historyId) {
          queryString = {
            ...queryString,
            historyId: historyId,
          };
        }
        break;
      case StompMessageKind.ALERT_RULE:
        path = `/alert/rules`;
        break;
      case StompMessageKind.RECORDER:
        path = `/recorders/list`;
        break;
      case StompMessageKind.FIRMWARE:
        path = `/recorders/list`;
        break;
      case StompMessageKind.CAMERA:
        path = `/recorders/list/cameras/${
          item.subKindId && item.kindId !== item.subKindId
            ? `${item.subKindId}/`
            : ""
        }${item.kindId}`;
        break;
      case StompMessageKind.RECORDER_GROUP:
        path = `/recorders/groups`;
        break;
      case StompMessageKind.USER:
        path = `/users/list`;
        break;
      case StompMessageKind.USER_GROUP:
        path = `/users/groups`;
        break;
      case StompMessageKind.ORGANIZATION:
        path = `/account`;
        break;
      case StompMessageKind.SUBSCRIPTION: {
        // 추후 naviagetSwitchAccount에서 to 경로 재설정할때 EU 경우 history 경로로 변경됨
        path = `/subscription/organization`;
        break;
      }
      case StompMessageKind.VIDEO_SHARE:
        item.eventType === NOTIFICATION_EVENT_TYPE.ADD && item.kindId
          ? (path = `/videoShare/${item.kindId}`)
          : (path = `/videoShare`);
        break;
      case StompMessageKind.REPORT:
        item.eventType === NOTIFICATION_EVENT_TYPE.ADD &&
        item.kindId &&
        item.subKind
          ? (path = `/reports/${item.subKind?.toLocaleLowerCase()}/${
              item.kindId
            }`)
          : (path = `/reports`);
        break;
      default:
        break;
    }
    if (path) {
      toggleOpen();
      navigateSwitchAccount(
        {
          accountId: selectAccountId,
          pathname: path,
          queryString: queryString,
        },
        false
      );
    }
  };

  //알림 드랍다운 열림/닫힘
  const toggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const mutationChangeAllRead = useStatusMutation(postAllRead, {
    onSuccess: () => {
      setIsSettingOpen(false);
      refetch();
    },
    onError: (error) => {
      console.error(error);
    },
  });
  //모든 알림 읽음 처리
  const onChangeAllRead = () => {
    if (!mutationChangeAllRead.idle) return;
    mutationChangeAllRead.mutate({
      email: user.email,
      isRead: true,
    });
  };
  useEffect(() => {
    //알림 드랍다운 열릴시 유저메뉴 열려있으면 닫기
    if (openUserPopup && isOpen) {
      dispatch(setIsPopup());
    }
  }, [isOpen, openUserPopup]);
  //알림 드랍다운 열려있을시 외부 클릭하면 닫기
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;
      const notificationDropdown = notificationDropdownRef.current;
      const notificationSetting = notificationSettingRef.current;

      const isClickOutsideDropdown = !notificationDropdown?.contains(target);
      const isClickOutsideSetting =
        isSettingOpen && !notificationSetting?.contains(target);
      const notificationButton = document.querySelector(
        "[data-testid='blocks-headernav-notification-button-toggle-open']"
      );
      if (notificationButton?.contains(target)) {
        return;
      }

      if (isOpen && isClickOutsideDropdown) {
        setIsOpen(false);
        setIsSettingOpen(false);
      } else if (isClickOutsideSetting && !isClickOutsideDropdown) {
        if (notificationButton?.contains(target)) {
          setIsOpen(false);
        }
        setIsSettingOpen(false);
      }
    },
    [isOpen, isSettingOpen, notificationDropdownRef, notificationSettingRef]
  );

  useEffect(() => {
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    data: infiniteData,
    isFetching,
    refetch,
  } = useInfiniteQuery(
    ["notification", user.email, isUnReadOnly, isOpen],
    ({ pageParam = 0 }: QueryFunctionContext) =>
      getNotification({
        ...baseQueryInfo,
        pageNum: pageParam ?? 0,
        sortType: "",
        sortDirection: SORT_DIRECTION.ASC,
        unReadOnly: isUnReadOnly,
      }),
    {
      getNextPageParam: (lastPage: NotificationListResponse | undefined) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: 0,
      initialPageParam: 0,
      enabled: !!user.email && isOpen,
    }
  );
  const onReadFirstItem = (item: NotificationItem) => {
    try {
      mutateSendNotificationFirstItem.mutate({
        email: user.email,
        notiId: item.notiId,
        notiTime: item.notiTime,
        isRead: true,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const data: NotificationItem[] = useMemo(() => {
    const firstItem = infiniteData?.pages[0]?.result[0];
    if (firstItem && !firstItem.isRead) {
      onReadFirstItem(firstItem);
    }
    return (
      infiniteData?.pages?.flatMap(
        (page: NotificationListResponse) => page.result
      ) ?? []
    );
  }, [infiniteData]);

  return (
    <NotificationContext.Provider
      value={{
        navigateToNotification,
        isReciveNotiCount,
        email: user.email,
        count,
        isOpen,
        setIsOpen,
        isUnReadOnly,
        setIsUnReadOnly,
        isSettingOpen,
        setIsSettingOpen,
        notificationDropdownRef,
        onChangeReadStatus,
        accountLevel: selectAccount.accountLevel,
        toggleOpen,
        onChangeAllRead,
        data,
        notificationSettingRef,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        refetch,
        isFetching,
      }}
    >
      <s.Wrapper>
        <NotificationButton />
        {isOpen && <NotificationPopup />}
      </s.Wrapper>
    </NotificationContext.Provider>
  );
}

interface NotificationContextType {
  navigateToNotification: (item: NotificationItem) => void;
  isReciveNotiCount: React.MutableRefObject<boolean>;
  email: string;
  count: number;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isUnReadOnly: boolean;
  setIsUnReadOnly: React.Dispatch<React.SetStateAction<boolean>>;
  isSettingOpen: boolean;
  setIsSettingOpen: React.Dispatch<React.SetStateAction<boolean>>;
  notificationDropdownRef: React.RefObject<HTMLDivElement>;
  onChangeReadStatus: (
    item: NotificationItem
  ) => Promise<NotificationReadMark | undefined>;
  accountLevel: LEVEL_TYPE;
  toggleOpen: () => void;
  onChangeAllRead: () => void;
  data: NotificationItem[];
  notificationSettingRef: React.RefObject<HTMLDivElement>;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  refetch: () => void;
  isFetching: boolean;
}

const NotificationContext = createContext<NotificationContextType>({
  navigateToNotification: () => {},
  isReciveNotiCount: {
    current: false,
  },
  email: "",
  count: 0,
  isOpen: false,
  setIsOpen: () => {},
  isUnReadOnly: false,
  setIsUnReadOnly: () => {},
  isSettingOpen: false,
  setIsSettingOpen: () => {},
  notificationDropdownRef: {
    current: null,
  },
  onChangeReadStatus: () => Promise.resolve(undefined),
  accountLevel: LEVEL_TYPE.INTL,
  toggleOpen: () => {},
  onChangeAllRead: () => {},
  data: [],
  notificationSettingRef: {
    current: null,
  },
  fetchNextPage: () => {},
  hasNextPage: false,
  isFetchingNextPage: false,
  isLoading: false,
  refetch: () => {},
  isFetching: false,
});

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};
